import PaginationComponent from '../Footers/Pagination.tsx';
import { Card, Empty, Tag, Typography, TableProps, Input, Button, DatePicker } from 'antd';
import { Link } from 'react-router-dom';
import { Lineitem } from '../../interfaces/Ordel.Interface';
import { ColumnsType, SorterResult } from 'antd/es/table/interface';
import { useNavigate } from 'react-router';
import { SalesOrderFilter } from '../../pages/Orders/SalesOrderFilter.tsx';
import { useQueryParameters } from '../../hooks';
import dayjs from 'dayjs';
import {
    LineItemQuery,
    useGetOrderLineItemsQuery,
} from '../../redux/api/order-line-item.ts';
import { useGetBuildingsQuery } from '../../redux/api/location.ts';
import { useCanViewRevenue } from '../../hooks/useCanViewRevenue.ts';
import BuildingTag from '../BuildingTag/index.tsx';
import { InternalTag } from './InternalTag';
import { getTagColor, getProductionMethodTagColor } from '../../utils/index.ts';
import { ShopShipDate, shopDueDate } from '../ShopShipDate';
import PartModelModal from '../PartModelViewer/PartModelModal.tsx';
import { useMemo, useState } from 'react';
import {
    CheckCircleFilled,
    InfoCircleFilled,
    WarningFilled,
} from '@ant-design/icons';

import CustomResponsiveTable from '../CustomResponsiveTable/index.tsx';

const { Text } = Typography;

interface IProps {
    filterByPreviouslyOrderedParts?: boolean;
    lineItemId?: string;
    hideFilters?: boolean;
}

const OrderLineItemsTable = (props: IProps) => {
    const navigate = useNavigate();
    const { data: buildings = [] } = useGetBuildingsQuery();
    const hasMultipleBuildings = buildings.length > 1;
    const canViewRevenue = useCanViewRevenue();
    const queryParams = useQueryParameters();
    const currentPageUrl = Number(queryParams.get('page')) || 1;
    const searchUrl = queryParams.get('search') || '';
    const ordering = queryParams.get('ordering') || '';
    const production_method = queryParams.get('production_method') || '';
    const status = queryParams.get('status') || '';
    const nameParam = queryParams.get('name') || '';
    const orderNameParam = queryParams.get('order_name') || '';
    const quantityParam = queryParams.get('quantity') || '';
    const materialStateParam = queryParams.get('material_state') || '';
    const buildingParam = queryParams.get('building') || '';
    const createdMin = queryParams.get('created_time_min') || '';
    const createdMax = queryParams.get('created_time_max') || '';
    const requiredMin = queryParams.get('required_partner_ship_date_min') || '';
    const requiredMax = queryParams.get('required_partner_ship_date_max') || '';
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedModel, setSelectedModel] = useState<Lineitem | null>(null);

    const lineItemQuery: LineItemQuery = {};
    if (currentPageUrl) {
        lineItemQuery.page = currentPageUrl;
    }

    if (searchUrl) {
        lineItemQuery.search = searchUrl;
    }

    if (production_method) {
        lineItemQuery.production_method = production_method;
    }

    if (nameParam) lineItemQuery.name = nameParam;
    if (orderNameParam) lineItemQuery.order_name = orderNameParam;
    if (quantityParam) lineItemQuery.quantity = quantityParam;
    if (materialStateParam) lineItemQuery.material_state = materialStateParam;
    if (buildingParam) lineItemQuery.building = buildingParam;
    if (createdMin) lineItemQuery.created_time_min = createdMin;
    if (createdMax) lineItemQuery.created_time_max = createdMax;
    if (requiredMin) lineItemQuery.required_partner_ship_date_min = requiredMin;
    if (requiredMax) lineItemQuery.required_partner_ship_date_max = requiredMax;

    if (ordering && ordering !== '') {
        lineItemQuery.ordering = ordering;
    }

    if (status && status !== '') {
        lineItemQuery.status = status;
    }

    // No longer filtering by separate delivery_status; use Status only

    if (props?.filterByPreviouslyOrderedParts && props?.lineItemId) {
        lineItemQuery.previously_ordered_parts = props?.lineItemId;
    }

    const { data, error } = useGetOrderLineItemsQuery(lineItemQuery,
        {
            refetchOnMountOrArgChange: true,
        }
    );

    if (error) {
        navigate('/orders/line-items', { replace: true });
    }

    const { count, results } = data ?? {
        count: 0,
        results: [],
    };

    const handleTableChange: TableProps<Lineitem>['onChange'] = (
        _pagination,
        _filters,
        sorter,
        extra
    ) => {
        if (_filters['status'] && _filters['status'].length > 0) {
            const selected: string[] = _filters['status'] as string[];
            if (selected.length > 0) {
                queryParams.set('status', selected.join(','));
            } else {
                queryParams.delete('status');
            }
        } else {
            queryParams.delete('status');
        }

        if (_filters['partsbadger_production_method'] && _filters['partsbadger_production_method'].length > 0) {
            queryParams.set('production_method', _filters['partsbadger_production_method'].toString());
        } else {
            queryParams.delete('production_method');
        }

        if (_filters['name'] && _filters['name'][0]) {
            queryParams.set('name', String(_filters['name'][0]));
        } else {
            queryParams.delete('name');
        }

        if (_filters['order_name'] && _filters['order_name'][0]) {
            queryParams.set('order_name', String(_filters['order_name'][0]));
        } else {
            queryParams.delete('order_name');
        }

        if (_filters['quantity'] && _filters['quantity'][0]) {
            queryParams.set('quantity', String(_filters['quantity'][0]));
        } else {
            queryParams.delete('quantity');
        }

        if (_filters['created_time'] && _filters['created_time'].length === 2) {
            const [min, max] = _filters['created_time'] as string[];
            if (min) queryParams.set('created_time_min', min); else queryParams.delete('created_time_min');
            if (max) queryParams.set('created_time_max', max); else queryParams.delete('created_time_max');
        } else {
            queryParams.delete('created_time_min');
            queryParams.delete('created_time_max');
        }

        if (_filters['required_partner_ship_date'] && _filters['required_partner_ship_date'].length === 2) {
            const [min, max] = _filters['required_partner_ship_date'] as string[];
            if (min) queryParams.set('required_partner_ship_date_min', min); else queryParams.delete('required_partner_ship_date_min');
            if (max) queryParams.set('required_partner_ship_date_max', max); else queryParams.delete('required_partner_ship_date_max');
        } else {
            queryParams.delete('required_partner_ship_date_min');
            queryParams.delete('required_partner_ship_date_max');
        }

        if (_filters['material_state'] && _filters['material_state'].length > 0) {
            queryParams.set('material_state', _filters['material_state'].toString()); // e.g. "selected,insufficient"
        } else {
            queryParams.delete('material_state');
        }

        if (_filters['building'] && _filters['building'].length > 0) {
            queryParams.set('building', _filters['building'].toString());
        } else {
            queryParams.delete('building');
        }
        queryParams.set('page', '1');

        if (extra.action === 'sort') {
            const orderType = (sorter as SorterResult<Lineitem>)?.order;
            const columnKey = (sorter as SorterResult<Lineitem>)?.columnKey;
            const sorterColumn = (sorter as SorterResult<Lineitem>)?.column;

            const column = orderType === 'ascend' || orderType === undefined ? columnKey : '-' + columnKey;
            if (column !== undefined && sorterColumn !== undefined) {
                queryParams.set('ordering', String(column));
            } else {
                queryParams.delete('ordering');
            }
        }

        navigate({ search: queryParams.toString() });
    };

    const showModal = (lineItem: Lineitem) => {
        setSelectedModel(lineItem);
        setIsModalOpen(true);
    };

    const handleClose = () => {
        setIsModalOpen(false);
    };

    const unique = (arr: (string | number | undefined | null)[]) =>
        Array.from(new Set(arr.filter((v) => v !== undefined && v !== null))) as (
            | string
            | number
        )[];

    const formatDate = (d?: string | null) =>
        d ? dayjs(d).format('YYYY-MM-DD') : '';

    const getMaterialStatus = (record: Lineitem): 'Selected' | 'Not Selected' | 'Insufficient Material' => {
        const hasAction =
            record.material_selection_status === 'Completed' ||
            record.material_reservations_count > 0;

        if (!hasAction) return 'Not Selected';
        if (
            record.material_reservations_count > 0 &&
            record.total_reserved_material_cut_quantity < record.quantity
        ) {
            return 'Insufficient Material';
        }
        return 'Selected';
    };

    // Helpers for filter dropdowns
    const renderTextFilter = (placeholder: string) => ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => (
        <div style={{ padding: 8, width: 180 }}>
            <Input
                placeholder={placeholder}
                value={selectedKeys?.[0]}
                onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                onPressEnter={() => confirm()}
                allowClear
                style={{ width: 160 }}
            />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', marginTop: 8 }}>
                <Button
                    disabled={!selectedKeys?.[0]}
                    type="text"
                    size="small"
                    style={{ color: selectedKeys?.[0] ? '#1677ff' : 'rgba(0, 0, 0, 0.25)' }}
                    onClick={() => {
                        setSelectedKeys([]);
                        clearFilters?.();
                        confirm();
                    }}
                >
                    Reset
                </Button>
                <Button type="primary" size="small" onClick={() => confirm()}>
                    OK
                </Button>
            </div>
        </div>
    );

    const renderDateRangeFilter = () => ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => {
        const RangePicker = DatePicker.RangePicker;
        const value = selectedKeys && selectedKeys.length === 2 && selectedKeys[0] && selectedKeys[1]
            ? [dayjs(selectedKeys[0] as string), dayjs(selectedKeys[1] as string)]
            : undefined;
        return (
            <div style={{ padding: 8 }}>
                <RangePicker
                    onChange={(dates) => {
                        if (!dates || dates.length !== 2) {
                            setSelectedKeys([]);
                        } else {
                            setSelectedKeys([
                                dates[0]?.format('YYYY-MM-DD') || '',
                                dates[1]?.format('YYYY-MM-DD') || '',
                            ]);
                        }
                    }}
                    value={value as any}
                    allowClear
                />
                <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', marginTop: 8 }}>
                    <Button
                        disabled={!selectedKeys?.[0]}
                        type="text"
                        size="small"
                        style={{ color: selectedKeys?.[0] ? '#1677ff' : 'rgba(0, 0, 0, 0.25)' }}
                        onClick={() => {
                            setSelectedKeys([]);
                            clearFilters?.();
                            confirm();
                        }}
                    >
                        Reset
                    </Button>
                    <Button type="primary" size="small" onClick={() => confirm()}>
                        OK
                    </Button>
                </div>
            </div>
        );
    };

    const methodFilters = useMemo(
        () =>
            [
                { text: 'Fastlane', value: 'Fastlane' },
                { text: 'QuickTurn', value: 'QuickTurn' },
                { text: 'Swiss', value: 'Swiss' },
                { text: 'Traditional', value: 'Traditional' },
                { text: 'No', value: 'No' },
            ],
        []
    );

    const materialFilters = useMemo(
        () =>
            [
                { text: 'Selected', value: 'selected' },
                { text: 'Insufficient Material', value: 'insufficient' },
                { text: 'Not Selected', value: 'not_selected' },
            ],
        []
    );

    const buildingFilters = useMemo(
        () =>
            [
                { text: 'No Building', value: '__none__' },
                ...buildings.map((item) => ({
                    text: item.name,
                    value: item.id,
                })),
            ],
        [buildings]
    );

    const receivedDateFilters = useMemo(
        () =>
            unique((results || []).map((r: Lineitem) => formatDate(r.created_time))).filter(
                (v) => v !== ''
            )
                .map((v) => ({ text: String(v), value: v })),
        [results]
    );

    const requiredShipDateFilters = useMemo(
        () =>
            unique(
                (results || []).map((r: Lineitem) =>
                    formatDate(r.required_partner_ship_date)
                )
            )
                .filter((v) => v !== '')
                .map((v) => ({ text: String(v), value: v })),
        [results]
    );

    const tableColumns: ColumnsType<any> = [
        {
            title: 'Preview',
            render: (record: Lineitem) => (
                <img
                    className="h-24"
                    src={record.thumbnail_file}
                    onClick={() => showModal(record)}
                    style={{ cursor: 'pointer' }}
                />
            ),
            key: 'thumbnail',
        },
        {
            title: 'Name',
            key: 'name',
            filterDropdown: renderTextFilter('Search name'),
            filteredValue: nameParam ? [nameParam] : null,
            render: (record: Lineitem) => (
                <div>
                    <Link to={`/orders/line-items/${record?.id}`}>{record.name}</Link>
                    <div className="mt-1">
                        <InternalTag isInternal={record.is_internal} />
                    </div>
                </div>
            ),
            sorter: (a, b) => {
                if (a.name < b.name) return -1;
                if (a.name > b.name) return 1;
                return 0;
            },
        },
        {
            title: 'Order',
            key: 'order_name',
            filterDropdown: renderTextFilter('Search order'),
            filteredValue: orderNameParam ? [orderNameParam] : null,
            render: (record: Lineitem) =>
                record.order?.id ? (
                    <Link to={`/orders/${record.order.id}`}>{record.order.name}</Link>
                ) : (
                    <Text type="secondary">—</Text>
                ),
            sorter: (a, b) => {
                const nameA = a.order?.name || '';
                const nameB = b.order?.name || '';
                if (nameA < nameB) return -1;
                if (nameA > nameB) return 1;
                return 0;
            },
        },
        ...(hasMultipleBuildings
            ? [
                  {
                      title: 'Building',
                      key: 'building',
                      filters: !props?.hideFilters ? buildingFilters : undefined,
                      filteredValue: buildingParam ? buildingParam.split(',') : null,
                      render: (record: Lineitem) =>
                          record.building ? (
                              <BuildingTag name={record.building.name} showIcon={false} />
                          ) : (
                              <Text type="secondary">—</Text>
                          ),
                  },
              ]
            : []),
        {
            title: 'Quantity',
            key: 'quantity',
            filterDropdown: renderTextFilter('Equals quantity'),
            filteredValue: quantityParam ? [quantityParam] : null,
            render: (record: Lineitem) => `${record.quantity}`,
            sorter: (a, b) => a.quantity - b.quantity,
        },
        {
            title: 'Method',
            key: 'partsbadger_production_method',
            filters: !props?.hideFilters ? methodFilters : undefined,
            filteredValue: production_method ? production_method.split(',') : null,
            // The server maps the "No" filter onto blank/NULL methods as well
            // (orders/filters.py::filter_production_method). Without mirroring
            // that here, this local predicate filtered those rows back out and
            // a line awaiting a method matched no filter option at all --
            // exactly the rows an operator is looking for (PTB-6779).
            onFilter: (value: boolean | React.Key, record: Lineitem) => {
                const method = (record.partsbadger_production_method || '').trim();
                if (String(value) === 'No') {
                    return method === '' || method.toLowerCase() === 'no';
                }
                return method === String(value);
            },
            render: (record: Lineitem) => (
                <Tag
                    color={getProductionMethodTagColor(
                        record.partsbadger_production_method
                    )}
                >
                    {record.partsbadger_production_method || 'Not Set'}
                </Tag>
            ),
            sorter: (a, b) => {
                if (a.partsbadger_production_method < b.partsbadger_production_method)
                    return -1;
                if (a.partsbadger_production_method > b.partsbadger_production_method)
                    return 1;
                return 0;
            },
        },
        {
            title: 'Material',
            key: 'material_state',
            filters: !props?.hideFilters ? materialFilters : undefined,
            filteredValue: materialStateParam ? materialStateParam.split(',') : null,
            render: (record: Lineitem) => {
                const hasAction =
                    record.material_selection_status === "Completed" ||
                    record.material_reservations_count > 0;

                if (!hasAction) {
                    return (
                        <Text type="danger">
                            <WarningFilled className="mr-2" />
                            Not Selected
                        </Text>
                    );
                }

                if (
                    record.material_reservations_count > 0 &&
                    record.total_reserved_material_cut_quantity < record.quantity
                ) {
                    return (
                        <Text type="warning">
                            <InfoCircleFilled className="mr-2" />
                            Insufficient Material
                        </Text>
                    );
                }

                return (
                    <Text type="success">
                        <CheckCircleFilled className="mr-2" />
                        Selected
                    </Text>
                );
            },
        },
        {
            title: 'Received Date',
            key: 'created_time',
            filterDropdown: renderDateRangeFilter(),
            filteredValue: createdMin || createdMax ? [createdMin, createdMax] : null,
            render: (record: Lineitem) =>
                record?.created_time
                    ? dayjs(record.created_time).format('YYYY-MM-DD')
                    : '',
            sorter: (a, b) => {
                const dateA = dayjs(a.created_time).format('YYYY-MM-DD') || '';
                const dateB = dayjs(b.created_time).format('YYYY-MM-DD') || '';
                return dateA.localeCompare(dateB);
            },
        },
        {
            title: 'Required Ship Date',
            key: 'required_partner_ship_date',
            filterDropdown: renderDateRangeFilter(),
            filteredValue: requiredMin || requiredMax ? [requiredMin, requiredMax] : null,
            render: (record: Lineitem) => (
                <ShopShipDate
                    original={record.required_partner_ship_date}
                    revised={record.revised_partner_ship_date}
                />
            ),
            sorter: (a, b) => {
                const dateA = shopDueDate(a)
                    ? dayjs(shopDueDate(a)).format('YYYY-MM-DD')
                    : '';
                const dateB = shopDueDate(b)
                    ? dayjs(shopDueDate(b)).format('YYYY-MM-DD')
                    : '';
                return dateA.localeCompare(dateB);
            },
        },
        ...(canViewRevenue
            ? [
                  {
                      title: 'Revenue',
                      key: 'revenue',
                      render: (record: Lineitem) =>
                          record?.revenue != null && String(record.revenue) !== ''
                              ? `$${Number(record.revenue).toFixed(2)}`
                              : (
                                  <Tag color="warning">No revenue</Tag>
                              ),
                      sorter: (a: Lineitem, b: Lineitem) =>
                          Number(a.revenue ?? 0) - Number(b.revenue ?? 0),
                  },
              ]
            : []),
        {
            title: 'Status',
            key: 'status',
            render: (record: Lineitem) => (
                <Tag color={getTagColor(record.status)}>{record.status}</Tag>
            ),
            sorter: true,
            onFilter: (value: boolean | React.Key, record) => {
                const v = String(value);
                const normalized = v === 'Ready for PBP' ? 'Ready For PBP' : v;
                return record.status === normalized;
            },
            filters: !props?.hideFilters
                ? [
                    { text: 'Received', value: 'Received' },
                    { text: 'In Progress', value: 'In Progress' },
                    { text: 'Delivered In Full', value: 'Delivered In Full' },
                    { text: 'Completed', value: 'Completed' },
                    { text: 'On Hold', value: 'On Hold' },
                    { text: 'Cancelled', value: 'Cancelled' },
                    { text: 'Ready for PBP', value: 'Ready For PBP' },
                ]
                : undefined,
            filteredValue: status ? status.split(',') : null,
        },
    ];

    const tableFooter = () => {
        return (
            <div className="flex justify-between items-center">
                <div className="ml-auto">
                    <PaginationComponent
                        pageSize={20}
                        total={count}
                        showSizeChanger={false}
                    />
                </div>
            </div>
        );
    };

    const emptyTable = () => {
        return (
            <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                    <span style={{ color: '#000' }}>
                        There are currently no line items to be displayed.
                    </span>
                }
            />
        );
    };

    return (
        <div
            data-testid="fastlane-order-line-items-table"
            data-ready={data ? 'true' : 'false'}
        >
            {!props?.hideFilters && (
                <div className="mb-4">
                    <SalesOrderFilter showProductionMethod={false} showFastlanePreset />
                </div>
            )}
            <Card className="shadow-sm" bodyStyle={{ padding: 0 }}>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <CustomResponsiveTable
                        columns={tableColumns}
                        data={results}
                        pagination={false}
                        loading={!data}
                        footer={tableFooter}
                        scroll={{ y: `calc(95vh - 250px)` }}
                        onRow={(record) => ({
                            'data-testid': 'fastlane-order-line-item-row',
                            'data-order-line-item-id': record.id,
                            'data-order-id': record.order?.id,
                        })}
                        onChange={handleTableChange}
                        emptyTable={emptyTable}
                    />
                </div>
            </Card>

            <PartModelModal
                open={isModalOpen}
                url={selectedModel?.file_3djs || null}
                partName={selectedModel?.name}
                onClose={handleClose}
            />
        </div>
    );
};

export default OrderLineItemsTable;
